import { ArrowRight, Check } from "lucide-react";
import { AUDIENCE } from "@/constants/testIds/landing";

const cards = [
  {
    id: 'pflegekraft',
    testId: AUDIENCE.cardPflegekraft,
    no: 'Nº 01',
    eyebrow: 'Für Pflegekräfte',
    badge: 'Bildungsgutschein',
    lines: ['Mehr Verantwortung.', 'Mehr Gehalt.', 'Mehr Möglichkeiten.'],
    benefits: [
      'Bildungsgutschein – meist 100 % gefördert',
      'Start jeden Montag · LG1, LG2, PDL',
      'AZAV-zertifizierte Abschlüsse',
    ],
    cta: 'Weiterbildungen entdecken',
    href: '#pflegekraft',
    image: '/images/hero-skandi-pflegekraft.png',
    imageAlt: 'Pflegekraft mit Zertifikatsmappe – Weiterbildung bei Dickies-Campus',
    objectPos: 'center 50%',
  },
  {
    id: 'einrichtung',
    testId: AUDIENCE.cardEinrichtung,
    no: 'Nº 02',
    eyebrow: 'Für ambulante Pflegedienste',
    badge: 'Team-Pakete',
    lines: ['Stärkere Teams.', 'Weniger Aufwand.', 'Planbare Fortbildung.'],
    benefits: [
      'Pflichtfortbildungen & MDK-konforme Doku',
      'Paketpreise für ganze Teams',
      'Online-Weiterbildung deutschlandweit',
    ],
    cta: 'Kooperationsmodell ansehen',
    href: '#einrichtung',
    image: '/images/hero-skandi-pflegedienst.png',
    imageAlt: 'Team eines ambulanten Pflegedienstes bei gemeinsamer Planung',
    objectPos: 'center 50%',
  },
];

const GRAIN = "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")";

export default function AudienceSplit() {
  return (
    <section id="audience-teaser" className="bg-cream-50 py-32 md:py-44">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.32em] font-bold text-sand-500 mb-12">
          <span>Dickies-Campus · MMXXVI</span>
          <span className="text-terra">Für wen wir da sind</span>
        </div>

        <div className="max-w-3xl mb-14 lg:mb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-[3px] w-12 bg-terra" />
            <span className="text-[11px] uppercase tracking-[0.28em] font-bold text-terra">
              Zwei Wege · Ein Ziel
            </span>
          </div>
          <h2 className="font-magazine font-medium text-[2.6rem] sm:text-[3.6rem] lg:text-[4.4rem] text-sand-800 leading-[1.08]">
            Zwei Wege.<br />
            Ein gemeinsames Ziel.<br />
            <span className="italic text-terra underline decoration-saffron-dark decoration-[3px] underline-offset-[10px]">Bessere Pflege.</span>
          </h2>
        </div>

        {/* Poster-Karten im Anzeigen-Stil */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {cards.map((c) => (
            <a
              key={c.id}
              href={c.href}
              data-testid={c.testId}
              className="group flex flex-col rounded-2xl bg-espresso-deep shadow-[0_25px_60px_-25px_rgba(27,26,24,0.5)] hover:-translate-y-1 transition-transform duration-500 overflow-hidden"
            >
              {/* Foto mit Editorial-Overlays */}
              <div className="relative h-56 md:h-64 overflow-hidden">
                <img
                  src={c.image}
                  alt={c.imageAlt}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.04]"
                  style={{ filter: "saturate(0.95) contrast(1.05) brightness(1.02)", objectPosition: c.objectPos }}
                />
                <div className="absolute inset-0 bg-terra/10 mix-blend-multiply pointer-events-none" />
                <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-[0.10] mix-blend-overlay" style={{ backgroundImage: GRAIN }} />

                <div className="absolute top-4 left-5 right-5 flex items-center justify-between text-[9px] uppercase tracking-[0.28em] font-bold text-cream-50 mix-blend-difference">
                  <span>Dickies-Campus</span>
                  <span>{c.no}</span>
                </div>
                <div className="absolute bottom-3 left-5">
                  <span className="inline-flex items-center rounded-full bg-saffron text-espresso-deep text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1.5 shadow">
                    {c.badge}
                  </span>
                </div>
              </div>

              {/* Cream-Panel — wie das Poster-Unterteil */}
              <div className="relative bg-cream-50 p-8 md:p-10 flex flex-col flex-1">
                <div className="absolute -top-[1px] left-8 h-[3px] w-14 bg-terra" />

                <div className="text-[10px] uppercase tracking-[0.28em] font-bold mb-4 text-terra">
                  {c.eyebrow}
                </div>

                <div className="font-magazine font-medium text-[1.8rem] md:text-[2rem] leading-[1.08] mb-7">
                  {c.lines.map((line, idx) => (
                    <div key={line} className={idx === c.lines.length - 1 ? "italic text-terra" : "text-sand-800"}>
                      {line}
                    </div>
                  ))}
                </div>

                <ul className="space-y-3 mb-9">
                  {c.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-[15px] text-sand-700 leading-snug">
                      <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-saffron text-espresso-deep shrink-0">
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                      <span className="font-medium">{b}</span>
                    </li>
                  ))}
                </ul>

                <span className="mt-auto inline-flex items-center gap-2 self-start rounded-full bg-espresso-deep text-cream-50 px-7 h-12 text-[12px] tracking-[0.18em] uppercase font-bold transition-colors duration-300 group-hover:bg-terra">
                  {c.cta}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2.2} />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
